import DataStructure, { sanitize } from "./DataStructure.js";
import { deepFreeze, timestampToDate } from "../util/misc.js";

export const MANAGER_SHARE_FIELDS = {
  // Reference to the sale this share is from
  saleID: null, // projectID + saleID
  projectID: null,

  // The salesperson who made the sale (not on manager's team)
  salespersonID: null,

  // Location where the sale was made
  locationID: null,

  // Share percentage the manager earns
  sharePercent: 0,

  // Sale values (copied from commission for calculation)
  soldDate: null,
  completedDate: null,
  expectedPreTax: null,
  actualPreTax: null,

  // Payment tracking
  paycheck: null,
  paid: false,

  // Meta info
  created: null,
  createdBy: null,
  modified: null,
  modifiedBy: null,
};
deepFreeze(MANAGER_SHARE_FIELDS);

export default class ManagerShare extends DataStructure {
  constructor(id, data = {}, userID = null) {
    data = sanitize(data, MANAGER_SHARE_FIELDS);

    // Convert to Date
    data.created = timestampToDate(data.created);
    data.modified = timestampToDate(data.modified);
    data.soldDate = timestampToDate(data.soldDate);
    data.completedDate = timestampToDate(data.completedDate);

    super(data);

    this.id = id || null;
    this.userID = userID; // The manager who receives this share
    this.permissions = Object.freeze({
      view: ManagerShare.canBeViewedBy,
      create: ManagerShare.canBeCreatedBy,
      edit: ManagerShare.canBeEditedBy,
      delete: ManagerShare.canBeDeletedBy,
    });
  }

  // Permissions

  static canBeViewedBy(user, doc) {
    if (user.isAdmin) return true;
    if (doc && doc.userID === user.id) return true;
    return false;
  }

  static canBeCreatedBy(user) {
    return user.isAdmin;
  }

  static canBeEditedBy(user) {
    return user.isAdmin;
  }

  static canBeDeletedBy(user) {
    return user.isAdmin;
  }

  // Database
  // Stored at: users/{managerID}/managerShares/{saleID}

  get path() {
    return ManagerShare.getPath(this.userID, this.id);
  }

  static getPath(userID, id) {
    if (userID && id) {
      return `users/${userID}/managerShares/${id}`;
    }
    if (userID) {
      return `users/${userID}/managerShares`;
    }
    return null;
  }

  static getStateID(path) {
    const match = path.match(/^users\/([^/]+)\/managerShares\/([^/]+)$/);
    if (match) {
      return match[2];
    }
    return null;
  }

  get stateID() {
    return this.id;
  }

  // Calculated values

  get isComplete() {
    return this.data.completedDate !== null;
  }

  get isPaid() {
    return this.data.paid;
  }

  // Expected share amount (before sale is complete)
  get expectedAmount() {
    if (this.data.expectedPreTax === null) return null;
    return (this.data.expectedPreTax * this.data.sharePercent) / 100;
  }

  // Actual share amount (after sale is complete)
  get actualAmount() {
    if (this.data.actualPreTax === null) return null;
    return (this.data.actualPreTax * this.data.sharePercent) / 100;
  }

  // Amount to display (actual if available, otherwise expected)
  get amount() {
    return this.actualAmount !== null ? this.actualAmount : this.expectedAmount;
  }

  // Firestore

  toFirestore() {
    return {
      ...this.data,
    };
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    // Extract userID from path
    const pathParts = snapshot.ref.path.split("/");
    const userID = pathParts[1];
    return new ManagerShare(snapshot.id, data, userID);
  }
}
